import { fbm2 } from '@vm/shared';
import { M } from '../palette';
import { blocks, MapBuilder, smoothField, stairRun, symPoint, symYaw, yawToward, type Brush } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
/** Plateau surface (first air) on both team sides. */
const P = 62;
const SYM = 'mirrorX' as const;
/** Cliff edge between the west plateau and the basin (before wobble). */
const EDGE = 170;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Where the cliff edge must stay straight: stairways and bridge landings. */
const straight = (z: number): number => {
  let d = 1;
  for (const [a, c] of [[150, 205], [230, 270], [370, 410], [435, 490], [490, 550]]) d = Math.min(d, clamp01(Math.max(a - z, z - c) / 24));
  return d;
};

function terrain(b: MapBuilder): void {
  const h = new Int16Array(S * S);
  const surface = new Uint8Array(S * S);
  const wob = smoothField(S, S, 4, (x, z) => fbm2(x * 0.02, z * 0.02, 51, 3));
  const tint = smoothField(S, S, 4, (x, z) => fbm2(x * 0.045, z * 0.045, 52, 2));
  const grass = [M('grass_4'), M('grass_2'), M('grass_4'), M('leaves_3')];
  const mud = M('mud');
  const rock = M('mossy_stone');
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const mx = x < S / 2 ? x : S - 1 - x;
      const w = wob(mx, z);
      const edge = EDGE + (w - 0.5) * 44 * straight(z);
      let y = mx < edge ? P : G;
      // North cliff (waterfall wall) and a lower south cliff close the basin.
      const north = 44 + (w - 0.5) * 30;
      const south = 604 + (w - 0.5) * 24;
      if (z < north) y = Math.max(y, 100 - Math.floor(Math.max(0, z - north + 16) / 4) * 4);
      if (z > south) y = Math.max(y, 82);
      const i = x + z * S;
      h[i] = y;
      const t = tint(mx, z);
      surface[i] = y > P ? rock : y === G && t > 0.64 ? mud : grass[Math.min(3, Math.floor(t * 4.4))];
    }
  b.heightfield({ height: h, surface, sub: [M('dirt_1'), M('dirt_2')], subDepth: 4, deep: M('rock_1') });
  // Exposed cliff faces become banded rock with a mossy lip (west half; the mirror copies it).
  const band = [M('stone_2'), M('stone_4'), M('stone_2'), M('stone_1')];
  const lip = M('mossy_stone');
  for (let z = 1; z < S - 1; z++)
    for (let x = 1; x < S / 2; x++) {
      const i = x + z * S;
      const hc = h[i];
      const hn = Math.min(h[i - 1], h[i + 1], h[i - S], h[i + S]);
      if (hn >= hc - 1) continue;
      for (let y = hn; y < hc - 1; y++) b.set(x, y, z, y >= hc - 7 ? lip : band[(y >> 3) & 3]);
    }
}

/** Tall jungle tree: straight trunk, buttress roots, flat layered canopy and hanging vines. */
function jungleTree(b: MapBuilder, x: number, y: number, z: number, h: number, r: number): void {
  const bark = M('log_2');
  const leaf = M('leaves_3');
  const leaf2 = M('leaves_1');
  b.box(x - 1, y, z - 1, x + 3, y + h, z + 3, bark);
  for (const [dx, dz] of [[-3, 0], [4, 0], [0, -3], [0, 4]]) b.box(x + dx, y, z + dz, x + dx + 2, y + 5, z + dz + 2, bark);
  const cy = y + h;
  b.octagon(x, z, cy - 4, cy, Math.round(r * 0.75), leaf);
  b.octagon(x, z, cy, cy + 5, r, leaf2);
  b.octagon(x + 2, z - 1, cy + 5, cy + 9, Math.round(r * 0.65), leaf);
  for (const [dx, dz] of [[-r + 1, 0], [r - 1, 2], [2, r - 1], [-1, -r + 1]]) {
    const len = 8 + ((x + z + dx) & 7);
    b.box(x + dx, cy - 4 - len, z + dz, x + dx + 1, cy - 4, z + dz + 1, leaf);
  }
}

/** Low fern clump. */
function fern(b: MapBuilder, x: number, y: number, z: number): void {
  b.octagon(x, z, y, y + 3, 4, M('leaves_4'));
  b.octagon(x, z, y + 3, y + 5, 2, M('leaves_4'));
}

/** Broken pillar (drum stack), some fallen. */
function pillar(b: MapBuilder, x: number, y: number, z: number, h: number, stone: Brush): void {
  b.box(x - 4, y, z - 4, x + 5, y + 3, z + 5, stone);
  b.octagon(x, z, y + 3, y + h, 3, stone);
  b.box(x - 1, y + h - 2, z - 1, x + 3, y + h + 1, z + 3, M('mossy_stone'));
}

/** Plank rope bridge between two points at the same height (deck top = y - 1), sagging in the middle. */
function ropeBridge(b: MapBuilder, x0: number, x1: number, z0: number, z1: number, y: number): void {
  const len = x1 - x0;
  const plank = M('planks_4');
  const rope = M('paint_brown');
  for (let i = 0; i < len; i++) {
    const t = i / len;
    const sag = Math.round(4 * 4 * t * (1 - t));
    const x = x0 + i;
    const dy = y - 1 - sag;
    if (i % 5 !== 4) b.box(x, dy, z0 + 1, x + 1, dy + 1, z1 - 1, plank);
    else b.box(x, dy, z0 + 1, x + 1, dy + 1, z1 - 1, M('planks_dark_2'));
    b.box(x, dy + 9 + Math.round(sag * 0.3), z0, x + 1, dy + 10 + Math.round(sag * 0.3), z0 + 1, rope);
    b.box(x, dy + 9 + Math.round(sag * 0.3), z1 - 1, x + 1, dy + 10 + Math.round(sag * 0.3), z1, rope);
    if (i % 8 === 0) {
      b.box(x, dy, z0, x + 1, dy + 10, z0 + 1, rope);
      b.box(x, dy, z1 - 1, x + 1, dy + 10, z1, rope);
    }
  }
  for (const x of [x0 - 2, x1]) for (const z of [z0 - 1, z1 - 1]) b.box(x, y - 4, z, x + 2, y + 12, z + 2, M('log_1'));
}

function temple(b: MapBuilder): void {
  const st = blocks([M('temple_1')], 3, 0);
  const st2 = blocks([M('temple_2')], 3, 0);
  const moss = M('mossy_stone');
  const C = 320;
  // Tiers: tier 1 reaches the plateau height so the terrace ring meets the rope bridges.
  const tiers: [number, number, number, Brush][] = [
    [90, G, P, st],
    [65, P, 92, st2],
    [40, 92, 117, st],
  ];
  for (const [r, y0, y1, br] of tiers) {
    b.box(C - r, y0, C - r, C + r, y1, C + r, br);
    // Moss creeping over the top edge of each tier.
    b.box(C - r, y1 - 1, C - r, C + r, y1, C - r + 3, moss);
    b.box(C - r, y1 - 1, C + r - 3, C + r, y1, C + r, moss);
    b.box(C - r, y1 - 1, C - r, C - r + 3, y1, C + r, moss);
    // Decorative band a meter below each top.
    b.box(C - r - 1, y1 - 8, C - r - 1, C + r + 1, y1 - 6, C + r + 1, st2);
  }
  b.box(C - 90, G - 1, C - 90, C + 90, G, C + 90, st2);

  // Ground-floor hall with four pillars, reached by north and south corridors.
  b.clear(C - 50, G, C - 50, C + 50, G + 28, C + 50);
  // Warm sandstone lining so the torch-lit hall does not read as a cave.
  b.walls(C - 51, G, C - 51, C + 51, G + 28, C + 51, 1, M('sandstone_1'));
  b.box(C - 51, G + 28, C - 51, C + 51, G + 29, C + 51, M('sandstone_2'));
  b.box(C - 50, G - 1, C - 50, C + 50, G, C + 50, M('sandstone_3'));
  b.walls(C - 42, G - 1, C - 42, C + 42, G, C + 42, 2, M('gold'));
  b.walls(C - 18, G - 1, C - 18, C + 18, G, C + 18, 2, M('jade'));
  for (const [dx, dz] of [[-30, -30], [20, -30], [-30, 20], [20, 20]]) {
    b.box(C + dx, G, C + dz, C + dx + 10, G + 28, C + dz + 10, M('sandstone_3'));
    b.box(C + dx - 1, G + 22, C + dz - 1, C + dx + 11, G + 24, C + dz + 11, M('jade'));
    b.box(C + dx - 1, G, C + dz - 1, C + dx + 11, G + 3, C + dz + 11, M('sandstone_2'));
    b.box(C + dx + 3, G + 14, C + dz - 1, C + dx + 7, G + 17, C + dz, M('neon_orange'));
    b.box(C + dx + 3, G + 14, C + dz + 10, C + dx + 7, G + 17, C + dz + 11, M('neon_orange'));
  }
  for (const zs of [-1, 1]) {
    const za = zs < 0 ? C - 90 : C + 50;
    b.clear(C - 10, G, za, C + 10, G + 24, za + 40);
    // Stepped lintel over the corridor mouth.
    const face = zs < 0 ? C - 90 : C + 89;
    b.box(C - 14, G + 24, face, C + 14, G + 28, face + 1, M('gold'));
  }
  // Braziers (glowing embers) along the hall walls.
  for (const [x, z] of [[C - 50, C - 20], [C - 50, C + 12], [C - 30, C + 42]]) {
    b.box(x, G + 12, z, x + 8, G + 14, z + 8, M('temple_2'));
    b.box(x + 2, G + 14, z + 2, x + 6, G + 17, z + 6, M('neon_orange'));
    b.box(x + 3, G + 17, z + 3, x + 5, G + 19, z + 5, M('neon_yellow'));
  }
  // A line of glowing gems along the walls just under the ceiling.
  for (let a = C - 44; a < C; a += 12) {
    b.box(C - 51, G + 25, a, C - 50, G + 27, a + 2, M('crystal'));
    b.box(a, G + 25, C - 51, a + 2, G + 27, C - 50, M('crystal'));
    b.box(a, G + 25, C + 50, a + 2, G + 27, C + 51, M('crystal'));
  }
  // Sun mural on the hall's west wall.
  b.glyph(C - 51, G + 22, C - 8, 'e', [
    '..yyyy..',
    '.yyggyy.',
    'yyg..gyy',
    'yg.jj.gy',
    'yg.jj.gy',
    'yyg..gyy',
    '.yyggyy.',
    '..yyyy..',
  ], { y: M('gold'), g: M('jade'), j: M('crystal') }, 2);

  // Tier-2 chamber, with a doorway onto the tier-1 terrace facing each team.
  b.clear(C - 40, P, C - 50, C + 40, P + 26, C + 50);
  b.walls(C - 41, P, C - 51, C + 41, P + 26, C + 51, 1, M('sandstone_1'));
  b.box(C - 40, P - 1, C - 50, C + 40, P, C + 50, M('sandstone_3'));
  for (let a = C - 36; a < C; a += 12) b.box(a, P + 20, C - 51, a + 3, P + 23, C - 50, M('neon_orange'));
  b.clear(C - 65, P, C - 38, C - 40, P + 22, C - 22);
  // Stairwell from the hall up into the chamber, one per side (the mirror copies it): a 34 degree flight along the hall's north wall region, running south
  // beside the pillars (x 30.3 to 31.5 m), from a 2.0 m foot at the north wall; the opening cuts the hall ceiling and the chamber floor over the upper steps,
  // and the top step is flush with the chamber floor. (The corridor mouth is at x 31.0 to 33.0 m: the foot is in front of it, the two flights flank the entrance.)
  b.clear(C - 19, G + 26, C - 21, C - 3, P, C + 18);
  b.flight(C - 17, G, C - 30, 's', 12, P - G, st2);
  b.box(C - 66, P + 22, C - 40, C - 64, P + 25, C - 20, M('gold'));
  // Treasure dais and jade statues in the chamber.
  b.box(C - 8, P, C + 30, C + 8, P + 3, C + 46, M('gold'));
  b.box(C - 3, P + 3, C + 36, C + 3, P + 9, C + 42, M('crystal'));
  b.statue(C - 30, P, C + 38, M('jade'));

  // Grand stairways to the summit shrine: a spiral up the west and east faces (the mirror copies it) in three 34 degree flights, one per tier, each running
  // along the face from a 2.0 m foot to a landing flush with the next terrace, between mossy rails: basin to the first terrace (rise 3.0 m), first to second
  // (3.0 m), second to the summit (2.5 m). A straight flight would need 12.9 m of run and the basin is 6 m wide.
  const climbs: [number, number, number, number, number][] = [
    // x of the flight (12 wide, against the face), start z of its first step (it runs north), base y, rise, landing depth.
    [C - 103, C + 29, G, P - G, 10],
    [C - 78, C + 54, P, 92 - P, 10],
    [C - 53, C + 44, 92, 117 - 92, 10],
  ];
  for (const [x, z, y, rise, land] of climbs) {
    const f = b.flight(x, y, z, 'n', 12, rise, st2);
    // The landing at the top: solid up to the terrace it opens onto, so you step off sideways onto the terrace.
    b.box(x, y, z - stairRun(f) - land + 1, x + 14, y + rise, z - stairRun(f) + 1, st2); // 14 wide: also over the 1 voxel band that runs round each tier
    b.flightRails(f, moss, x < C - 90 ? ['lo'] : ['lo']);
  }

  // Summit shrine with a stepped roof and a glowing gem.
  const sy = 117;
  b.box(C - 26, sy, C - 26, C + 26, sy + 24, C + 26, st2);
  b.clear(C - 23, sy, C - 23, C + 23, sy + 22, C + 23);
  for (const [a, c] of [[C - 26, C - 23], [C + 23, C + 26]]) {
    b.clear(a, sy, C - 8, c, sy + 18, C + 8);
    b.clear(C - 8, sy, a, C + 8, sy + 18, c);
  }
  for (let k = 0; k < 5; k++) b.box(C - 30 + k * 5, sy + 24 + k * 3, C - 30 + k * 5, C + 30 - k * 5, sy + 27 + k * 3, C + 30 - k * 5, k % 2 ? st : moss);
  b.box(C - 3, sy + 39, C - 3, C + 3, sy + 46, C + 3, M('gold'));
  b.box(C - 3, sy, C - 3, C + 3, sy + 4, C + 3, st);
  b.box(C - 2, sy + 4, C - 2, C + 2, sy + 8, C + 2, M('crystal'));

  // Vines hanging from the terraces.
  for (const [x, z, y, len] of [[C - 90, C - 60, P - 1, 18], [C - 90, C + 53, P - 1, 24], [C - 65, C - 40, 91, 14], [C - 88, C + 70, P - 1, 12], [C - 40, C - 90, P - 1, 20]]) {
    b.box(x - 1, y - len, z, x, y, z + 1, M('leaves_3'));
    b.box(x - 1, y - len + 4, z + 3, x, y, z + 4, M('leaves_1'));
  }
}

function westHalf(b: MapBuilder): void {
  const st = blocks([M('temple_1')], 3, 3);
  const st2 = blocks([M('temple_2')], 3, 4);
  // Stone stairways down the west cliff: 34 degree flights whose top end is where the old steep ones ended (cut into the plateau), the first step 1.5 m
  // further out in the basin so there is a 2.0 m foot in front of it.
  for (const z0 of [160, 446]) {
    const f = b.climbEnding(EDGE + 23, G, z0, 'w', 34, P - G, st2);
    b.trenchAbove(f, P + 4);
    b.box(EDGE - 8, G, z0 - 2, EDGE + 40, P + 3, z0, M('mossy_stone'));
    b.box(EDGE - 8, G, z0 + 34, EDGE + 40, P + 3, z0 + 36, M('mossy_stone'));
  }
  // Rope bridges from the plateau to the temple's first terrace.
  for (const z0 of [238, 390]) {
    b.clear(EDGE - 12, P, z0 - 2, 232, P + 14, z0 + 14);
    b.box(EDGE - 12, P - 1, z0, EDGE, P, z0 + 12, M('temple_2'));
    ropeBridge(b, EDGE, 230, z0, z0 + 12, P);
  }

  // Team spawn: ruined courtyard at the back of the west plateau.
  b.box(18, P - 1, 272, 92, P, 368, blocks([M('temple_2'), M('mossy_stone')], 3, 5));
  const wall = st;
  b.box(92, P, 262, 98, P + 20, 300, wall);
  b.box(92, P, 340, 98, P + 16, 378, wall);
  b.box(18, P, 262, 70, P + 14, 268, wall);
  b.box(30, P, 372, 98, P + 12, 378, wall);
  b.box(92, P + 20, 262, 98, P + 24, 280, M('mossy_stone'));
  b.box(60, P, 306, 66, P + 18, 334, st2);
  b.box(8, P, 290, 10, P + 36, 350, M('team_red'));
  b.box(6, P + 36, 286, 12, P + 40, 354, M('gold'));

  // Point A: ring of broken pillars around a paved plaza.
  b.box(90, P - 1, 150, 160, P, 220, blocks([M('temple_1')], 3, 6));
  const ring: [number, number, number][] = [[96, 156, 26], [150, 158, 14], [96, 212, 10], [152, 212, 30], [124, 150, 20], [124, 218, 8]];
  for (const [x, z, hh] of ring) pillar(b, x, P, z, hh, st);
  b.box(106, P, 190, 132, P + 4, 196, st2);
  // Explorer camp with a tent, crates and explosive supplies.
  b.tent(40, P, 120, true, 34, 26, M('cloth_green'));
  b.crate(84, P, 118);
  b.crate(84, P + 10, 120, 8);
  b.tntCrate(96, P, 104, 7);
  // Overgrown shrine in the south-west.
  b.building({
    x0: 52, x1: 112, z0: 474, z1: 530, y0: P - 1, floors: 1, floorH: 28, wall: st, roof: M('mossy_stone'), roofStyle: 'flat', parapet: 3,
    trim: M('temple_2'), windows: { w: 6, h: 8, sill: 10, spacing: 16, glass: null }, doors: [{ side: 'n', w: 12 }, { side: 'e', w: 12 }],
  });
  b.box(76, P, 500, 88, P + 3, 512, M('gold'));
  b.box(79, P + 3, 503, 85, P + 9, 509, M('jade'));

  // Jungle on the plateau and in the basin.
  const trees: [number, number, number, number][] = [
    [30, 40, 70, 16], [110, 70, 60, 14], [40, 200, 64, 15], [150, 110, 54, 12], [30, 430, 66, 15], [124, 420, 58, 13],
    [150, 580, 62, 14], [40, 590, 58, 13], [200, 120, 64, 15], [212, 560, 60, 14], [196, 290, 56, 12], [140, 290, 50, 11],
  ];
  for (const [x, z, hh, r] of trees) jungleTree(b, x, b.surfaceY(x, z, 110), z, hh, r);
  b.leafyTree(250, G, 470, 48, 15, M('leaves_2'));
  b.leafyTree(70, P, 250, 44, 14, M('leaves_2'));
  const ferns: [number, number][] = [[60, 60], [80, 160], [20, 250], [130, 250], [150, 350], [20, 400], [100, 440], [150, 520], [200, 200], [224, 440], [200, 380], [60, 560]];
  for (const [x, z] of ferns) fern(b, x, b.surfaceY(x, z, 110), z);
  for (const [x, z, r] of [[160, 380, 7], [230, 130, 9], [206, 520, 8], [20, 150, 8]] as const) b.rock(x, b.surfaceY(x, z, 110) - 2, z, r, M('mossy_stone'));
  // Fallen log across the basin path and broken pillars by the cliff.
  b.line(186, G, 356, 222, G + 2, 372, M('log_1'), 4);
  pillar(b, 200, G, 250, 18, st);
  pillar(b, 214, G, 420, 26, st);

  // Waterfall pool at the foot of the north cliff (west half; the mirror completes it).
  for (let z = 40; z < 140; z++)
    for (let x = 250; x < 320; x++) {
      const d = Math.hypot((x - 319.5) / 1.3, z - 88);
      if (d > 46) continue;
      if (d > 40) {
        b.box(x, G, z, x + 1, G + 3, z + 1, M('mossy_stone'));
        continue;
      }
      const top = b.surfaceY(x, z, 110);
      b.clear(x, G - 7, z, x + 1, Math.max(top, G), z + 1);
      b.box(x, G - 8, z, x + 1, G - 7, z + 1, d > 30 ? M('sand_2') : M('sand_1'));
      b.box(x, G - 7, z, x + 1, G - 1, z + 1, M('water'));
      if (((x * 7 + z * 13) % 97 === 0 || (x * 11 + z * 5) % 89 === 0) && d < 34 && d > 12) b.box(x - 1, G - 1, z - 1, x + 2, G, z + 2, M('leaves_4'));
    }
  // Falling sheet of water down the cliff, a channel on top and foam at the bottom.
  // Waterfall: a notch in the cliff, a sheet that thickens as it falls, foam where it lands.
  b.clear(296, 84, 0, 320, 101, 40);
  b.box(296, 83, 0, 320, 84, 40, M('mossy_stone'));
  b.box(300, 84, 0, 320, 92, 36, M('water'));
  // Slot cut into the cliff face so the whole sheet shows, with a rock wall behind it.
  b.clear(300, G, 30, 320, 84, 60);
  b.box(296, G, 26, 320, 84, 34, M('mossy_stone'));
  for (let y = G - 1; y < 92; y++) {
    const t = (92 - y) / (92 - G);
    const depth = 3 + Math.round(t * t * 8);
    const inset = (y * 5) % 7 === 0 ? 1 : 0;
    b.box(300 + inset, y, 34, 320, y + 1, 34 + depth, M('water'));
  }
  for (const [x, z, r] of [[296, 42, 5], [290, 58, 4], [312, 60, 3]] as const) b.rock(x, G - 2, z, r, M('mossy_stone'));
  b.box(298, G - 1, 46, 320, G, 60, M('snow_1'));
  b.box(302, G, 48, 320, G + 1, 56, M('snow_1'));
  b.box(306, G + 1, 50, 320, G + 2, 54, M('snow_2'));

  // South arch bridge between the plateaus on two piers.
  const bz0 = 500;
  const bz1 = 540;
  b.box(EDGE - 10, P - 3, bz0, 320, P, bz1, st2);
  b.box(EDGE - 10, P, bz0, 320, P + 7, bz0 + 3, st);
  b.box(EDGE - 10, P, bz1 - 3, 320, P + 7, bz1, st);
  b.box(262, G, bz0 + 4, 282, P - 3, bz1 - 4, st);
  // Arches between the cliff, the pier and the middle.
  for (let x = EDGE; x < 320; x++) {
    const inA = x < 262;
    const a0 = inA ? EDGE : 282;
    const a1 = inA ? 262 : 358;
    const t = (x - a0) / (a1 - a0);
    const rise = Math.round(Math.sqrt(Math.max(0, 1 - (2 * t - 1) * (2 * t - 1))) * 18);
    b.box(x, P - 3 - Math.max(3, 14 - rise), bz0 + 2, x + 1, P - 3, bz1 - 2, st);
  }
  b.tntCrate(284, G, 506, 7);
}

function build(b: MapBuilder): MapMeta {
  terrain(b);
  westHalf(b);
  temple(b);
  b.symmetrize(SYM);

  // Metadata ------------------------------------------------------------------------------
  const py = P / 10;
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const team0: [number, number][] = [
    [2.8, 29], [2.8, 31.5], [2.8, 34], [2.8, 36.5], [4.8, 28], [4.8, 30.5], [4.8, 33], [4.8, 35.5],
  ];
  for (const [x, z] of team0) {
    const yaw = -Math.PI / 2;
    meta.spawns.push({ team: 0, x, y: py, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: py, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffa: [number, number][] = [
    [4, 8], [60, 8], [4, 56], [60, 56], [15, 24], [49, 24], [15, 44], [49, 44], [21, 16], [43, 16], [21, 47], [43, 47],
    [27, 12], [37, 12], [24, 57], [40, 57], [10, 42], [54, 42],
  ];
  // Search down from just above the plateau so tree canopies are never mistaken for ground.
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 7), z, yaw: yawToward(x, z, 32, 32) });
  meta.points.push({ id: 'A', name: 'West Ruins', x: 12.5, y: py, z: 17.2, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Temple Hall', x: 32, y: gy, z: 32, r: 3.5 });
  meta.points.push({ id: 'C', name: 'East Ruins', x: 51.5, y: py, z: 17.2, r: 3.5 });
  meta.flags.push({ team: 0, x: 7.5, y: py, z: 32 }, { team: 1, x: 56.5, y: py, z: 32 });
  for (const [x, z, kind] of [[12.4, 23.6, 'health'], [51.6, 23.6, 'health'], [22, 47, 'armor'], [42, 47, 'armor'], [32, 18, 'health']] as const)
    meta.pickups.push({ kind, x, y: b.standAt(x, z, 7), z });
  meta.pickups.push({ kind: 'health', x: 28, y: gy, z: 36 }, { kind: 'health', x: 36, y: gy, z: 36 });
  return meta;
}

export const sunkenTemple: MapDef = {
  id: 'sunken_temple',
  name: 'Sunken Temple',
  description: 'A stepped temple in a jungle basin, rope bridges from the ruined plateaus and a waterfall roaring behind.',
  size: [S, 192, S],
  modes: ['tdm', 'dom', 'ffa', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 777,
  version: 2,
  theme: {
    skyTop: 0x5f9ad6,
    skyHorizon: 0xd8e8d0,
    fog: 0xc0d6c0,
    fogNear: 34,
    fogFar: 120,
    sun: [0.35, 0.8, 0.3],
    sunColor: 0xfff0d0,
    sunIntensity: 0.95,
    ambient: 0xc4d4e8,
    ambientIntensity: 0.62,
    outside: 0x3f7a2a,
    music: 'jungle',
  },
  build,
};
